import { memo } from "react";
import { Controller, useFormContext } from "react-hook-form";
import { FormGroup, Label, FormFeedback, Button } from "reactstrap";

type Props = {
  name: string;
  label: string;
  accept?: string;
  multiple?: boolean;
  required?: boolean;
  className?: string;
  resolveFileUrl?: (value: string) => string;
};

const RHFFileInput = memo(function RHFFileInput({
  name,
  label,
  accept = ".pdf,.jpg,.jpeg,.png",
  multiple = false,
  required = false,
  className,
  resolveFileUrl,
}: Props) {
  const { control } = useFormContext();

  return (
    <Controller
      name={name}
      control={control}
      defaultValue={[]} // ensures consistent default
      render={({
        field: { value = [], onChange, ref },
        fieldState: { error },
      }) => {
        // Normalize to array
        const filesArray = Array.isArray(value) ? value : value ? [value] : [];

        const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
          const selected = e.target.files ? Array.from(e.target.files) : [];
          const newFiles = multiple ? [...filesArray, ...selected] : selected;
          onChange(newFiles);
          e.target.value = ""; // reset native input
        };

        const handleRemove = (index: number) => {
          const newFiles = filesArray.filter((_, i) => i !== index);
          onChange(newFiles);
        };

        return (
          <FormGroup className={className}>
            {label && <Label className="form-label">{label}</Label>}
            {label && required && (
              <span
                className="ms-1 px-1 py-0 rounded-1 f-10 fw-bold"
                style={{
                  background: "#FEE2E2",
                  color: "#B91C1C",
                  verticalAlign: "middle",
                }}
              >
                required
              </span>
            )}
            <input
              key={filesArray.length || 0}
              id={name}
              ref={ref}
              type="file"
              accept={accept}
              multiple={multiple}
              onChange={handleFileChange}
              className={`form-control ${error ? "is-invalid" : ""}`}
            />
            {error &&
              Array.isArray(error) &&
              error.map((d) => (
                <FormFeedback className="mb-2">{d.message}</FormFeedback>
              ))}{" "}
            {error && (
              <FormFeedback className="mb-2">{error.message}</FormFeedback>
            )}
            {filesArray.length > 0 && (
              <ul className="list-unstyled mt-2">
                {filesArray.map((item: File | string, index: number) => {
                  const isFile = item instanceof File;
                  const fileName = isFile
                    ? item.name
                    : String(item).split("/").pop();
                  const fileUrl = isFile
                    ? URL.createObjectURL(item)
                    : resolveFileUrl
                      ? resolveFileUrl(item as string)
                      : (item as string);

                  return (
                    <li
                      key={`${fileName}-${index}`}
                      className="d-flex align-items-center justify-content-between border rounded p-2 mb-1"
                    >
                      <div className="d-flex align-items-center gap-2">
                        {isFile && item.type.startsWith("image/") ? (
                          <img
                            src={fileUrl}
                            alt={fileName}
                            width={50}
                            height={50}
                            style={{ objectFit: "cover", borderRadius: "4px" }}
                          />
                        ) : (
                          <i className="fa fa-file-text text-secondary fs-4" />
                        )}
                        <a
                          href={fileUrl}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="text-truncate"
                          style={{ maxWidth: "160px" }}
                        >
                          {fileName}
                        </a>
                      </div>
                      <Button
                        type="button"
                        color="danger"
                        onClick={() => handleRemove(index)}
                        style={{
                          width: "25px",
                          height: "25px",
                          padding: 0,
                          borderRadius: "50%",
                        }}
                      >
                        ×
                      </Button>
                    </li>
                  );
                })}
              </ul>
            )}
          </FormGroup>
        );
      }}
    />
  );
});

export default RHFFileInput;
